import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import Reveal from "../components/Reveal";
import SEO from "../components/SEO";
import SpecialOrderModal from "../components/SpecialOrderModal";
import { PRODUCT_BY_SLUG, PRODUCTS, formatPrice } from "../lib/pulaar";
import { useCart } from "../context/CartContext";

export default function Product() {
  const { slug } = useParams();
  const product = PRODUCT_BY_SLUG[slug];
  const [size] = useState("unique");
  const [openAcc, setOpenAcc] = useState("histoire");
  const [orderOpen, setOrderOpen] = useState(false);
  const { addItem } = useCart();
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [slug]);

  if (!product) return <Navigate to="/vestiaire" replace />;

  const SHARED_ACCORDIONS = [
    {
      id: "livraison",
      title: t("product.accordionShipping"),
      body: t("product.shippingText"),
    },
    {
      id: "entretien",
      title: t("product.accordionMaterials"),
      body:
        t("product.materialsText", {
          defaultValue:
            "Lavage à la main ou pressing recommandé. Repassage doux à l'envers. Pour préserver la matière, éviter l'exposition prolongée au soleil et privilégier un séchage à plat.",
        }),
    },
  ];

  // Statuts conservés : "soon" (pré-launch) et "available" (Saison 01).
  // Les statuts "sold_out" et "on_order" ont été retirés (jamais utilisés).
  const isSoon = product.status === "soon";

  const handleAdd = () => addItem(product, size);
  const handleOrder = () => setOrderOpen(true);

  const related = PRODUCTS.filter(
    (p) => p.category === product.category && p.slug !== product.slug
  ).slice(0, 3);

  return (
    <main data-testid={`product-page-${product.slug}`} className="bg-[#FBF8EF] pt-28 md:pt-32 pb-24">
      <SEO
        title={product.name}
        description={`${product.name} — ${product.short.slice(0, 160)}`}
        path={`/vestiaire/${product.slug}`}
        image={product.image}
        imageAlt={`Pulaar — ${product.name}`}
        type="product"
      />
      <div className="mx-auto max-w-[1700px] px-4 md:px-10 lg:px-14">
        {/* breadcrumb — nom du produit flouté pendant le pré-launch */}
        <div className="text-[10px] tracking-[0.28em] uppercase text-[#4A4641] mb-8 flex gap-3 items-center">
          <Link to="/vestiaire" className="link-underline">Vestiaire</Link>
          <span>/</span>
          <span
            data-testid="breadcrumb-product-name"
            className="text-[#121212] select-none"
            style={{ filter: "blur(6px)", pointerEvents: "none" }}
            aria-hidden="true"
          >
            {product.name}
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* LEFT — info (FLOUTÉ : SOON, on cache les détails) */}
          <aside
            data-testid="product-info-blurred"
            className="lg:col-span-3 lg:sticky lg:top-28 self-start order-2 lg:order-1 select-none"
            style={{ filter: "blur(16px)", pointerEvents: "none" }}
            aria-hidden="true"
          >
            <Reveal>
              <p className="text-[10px] tracking-[0.28em] uppercase text-[#5C4033] mb-3">
                Pulaar · Pièce
              </p>
              <h1
                data-testid="product-name"
                className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-[-0.02em] text-[#121212] leading-[1.05]"
              >
                {product.name}
              </h1>
              <p className="mt-5 text-sm text-[#4A4641] leading-[1.8]">{product.short}</p>
              <p
                data-testid="product-price"
                className="mt-6 font-serif text-2xl text-[#121212]"
              >
                {formatPrice(product.price)}
              </p>

              <dl className="mt-10 space-y-5 text-sm">
                {[
                  ["Matière", product.matiere],
                  ["Origine", product.origine],
                  ["Fabrication", product.fabrication],
                  ["Détails", product.details],
                ].map(([k, v]) => (
                  <div key={k} className="border-t border-[#E5DFD3] pt-4">
                    <dt className="text-[10px] tracking-[0.28em] uppercase text-[#5C4033] mb-1">
                      {k}
                    </dt>
                    <dd className="text-[#121212] leading-[1.7]">{v}</dd>
                  </div>
                ))}
              </dl>
            </Reveal>
          </aside>

          {/* CENTER — gallery (FLOUTÉ : SOON) */}
          <div
            data-testid="product-gallery-blurred"
            className="lg:col-span-6 order-1 lg:order-2 space-y-3 md:space-y-4 select-none"
          >
            {product.gallery.map((src, i) => (
              <Reveal key={src} delay={i * 0.05} y={30}>
                <div className="overflow-hidden bg-[#F3EFE7] aspect-[3/4]">
                  <img
                    src={src}
                    alt={`${product.name} ${i + 1}`}
                    loading={i === 0 ? "eager" : "lazy"}
                    draggable="false"
                    onContextMenu={(e) => e.preventDefault()}
                    className="w-full h-full object-cover pointer-events-none select-none"
                    style={{ filter: "blur(18px)" }}
                  />
                </div>
              </Reveal>
            ))}
          </div>

          {/* RIGHT — order */}
          <aside className="lg:col-span-3 lg:sticky lg:top-28 self-start order-3">
            <Reveal>
              <div className="border border-[#E5DFD3] bg-[#FBF8EF] p-6 md:p-7">
                <div className="flex items-center justify-between mb-6">
                  <span className="text-[10px] tracking-[0.28em] uppercase text-[#5C4033]">
                    {t("product.statusLabel", { defaultValue: "Statut" })}
                  </span>
                  <span
                    data-testid="product-status"
                    className={`text-[10px] tracking-[0.28em] uppercase ${
                      isSoon ? "text-[#5C4033]" : "text-[#2F6B3F]"
                    }`}
                  >
                    {t(`product.status${product.status.charAt(0).toUpperCase() + product.status.slice(1)}`, { defaultValue: product.status })}
                  </span>
                </div>

                <p className="text-[10px] tracking-[0.28em] uppercase text-[#5C4033] mb-3">
                  {t("product.sizeLabel")}
                </p>
                <div
                  data-testid="product-size-unique"
                  className="h-12 flex items-center justify-center border border-[#E5DFD3] text-[#121212] text-[11px] tracking-[0.32em] uppercase bg-[#F3EFE7]"
                >
                  {t("cart.sizeUnique")}
                </div>

                <div className="mt-6 space-y-3">
                  {isSoon ? (
                    <>
                      <p className="text-xs text-[#4A4641] mb-2">
                        {t("product.soonNote", { defaultValue: "Saison 01 — bientôt en atelier." })}
                      </p>
                      <button
                        onClick={handleOrder}
                        data-testid="product-inform-btn"
                        className="w-full h-12 bg-[#121212] text-[#FBF8EF] text-[11px] tracking-[0.32em] uppercase hover:bg-[#5C4033] transition-colors duration-500"
                      >
                        {t("product.soonCta")}
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={handleAdd}
                      data-testid="product-add-btn"
                      className="w-full h-12 bg-[#121212] text-[#FBF8EF] text-[11px] tracking-[0.32em] uppercase hover:bg-[#5C4033] transition-colors duration-500"
                    >
                      {t("product.addToCart")}
                    </button>
                  )}
                </div>

                <p className="mt-5 text-xs text-[#4A4641] leading-[1.7]">
                  {t("product.fabricationNote", { defaultValue: "Confection à la commande sous 3 à 4 semaines." })}
                </p>
                <Link
                  to="/a-propos#contact"
                  data-testid="product-contact-link"
                  className="mt-3 inline-block text-[11px] tracking-[0.28em] uppercase text-[#5C4033] link-underline"
                >
                  {t("contact.title")} {t("contact.titleEm")}
                </Link>
              </div>

              {/* Accordion */}
              <div className="mt-8 border-t border-[#E5DFD3]">
                {[
                  {
                    id: "histoire",
                    title: t("product.accordionStory"),
                    body: product.histoire,
                  },
                  ...SHARED_ACCORDIONS,
                ].map((a) => {
                  const isOpen = openAcc === a.id;
                  return (
                    <div key={a.id} className="border-b border-[#E5DFD3]">
                      <button
                        onClick={() => setOpenAcc(isOpen ? null : a.id)}
                        data-testid={`acc-${a.id}`}
                        className="w-full flex items-center justify-between py-5 text-left"
                      >
                        <span className="text-[11px] tracking-[0.28em] uppercase text-[#121212]">
                          {a.title}
                        </span>
                        <ChevronDown
                          size={16}
                          strokeWidth={1.2}
                          className={`text-[#5C4033] transition-transform duration-500 ${
                            isOpen ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                      <AnimatePresence initial={false}>
                        {isOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                            className="overflow-hidden"
                          >
                            <p
                              data-testid={`acc-body-${a.id}`}
                              className="pb-6 text-sm leading-[1.8] text-[#4A4641] select-none"
                              style={a.id === "histoire" ? { filter: "blur(14px)", pointerEvents: "none" } : undefined}
                              aria-hidden={a.id === "histoire" ? "true" : undefined}
                            >
                              {a.body}
                            </p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </Reveal>
          </aside>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <section className="mt-32 md:mt-40 border-t border-[#E5DFD3] pt-16">
            <h3 className="font-serif text-2xl md:text-4xl tracking-[-0.02em] text-[#121212] mb-10">
              D’autres <span className="italic font-light">pièces</span>
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-5 md:gap-x-8 gap-y-12">
              {related.map((p) => (
                <Link
                  key={p.slug}
                  to={`/vestiaire/${p.slug}`}
                  data-testid={`related-product-${p.slug}`}
                  className="group block"
                >
                  <div className="overflow-hidden aspect-[3/4] bg-[#F3EFE7]">
                    <img
                      src={p.image}
                      alt={p.name}
                      draggable="false"
                      onContextMenu={(e) => e.preventDefault()}
                      className="w-full h-full object-cover transition-all duration-[1400ms] ease-out group-hover:scale-[1.02] blur-[18px] pointer-events-none select-none"
                    />
                  </div>
                  <div className="mt-3 flex items-baseline justify-between blur-[14px] opacity-70 select-none">
                    <h4 className="font-serif text-lg text-[#121212]">{p.name}</h4>
                    <span className="text-sm text-[#4A4641]">{formatPrice(p.price)}</span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
      <SpecialOrderModal
        open={orderOpen}
        onClose={() => setOrderOpen(false)}
        product={product}
        defaultSize={size}
      />
    </main>
  );
}
